import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swiper from 'swiper'
import 'swiper/css'
import Button from '@renderer/components/bahela/Button'
import Preview from '@renderer/components/Preview'
import bgLayer from '/assets/bahela/bg-layer.webp'
import backButton from '/assets/bahela/back-button-blue.svg'
import arrowBlue from '/assets/bahela/arrow-blue.svg'
import pickRollId from '/assets/bahela/pick-roll-id.webp'
import pickRollEn from '/assets/bahela/pick-roll-en.webp'
import { Countdown } from '@renderer/utils/countdownUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import QuitModal from '@renderer/components/bahela/QuitModal'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useKotakCropStore, kotakSides } from '@renderer/stores/kotakCropStore'

/**
 * Bahelakotak filter picker — forked from bahela/Filter so each filter thumbnail previews the
 * SAME cropped box the capture page uses (per-project kotak crop rect + ratio), instead of a
 * generic centered square. Keeps the whole kotak flow (camera → filter → capture) visually
 * consistent. Uses the left-box camera (honoring the operator L/R swap) as the representative feed.
 */
const FilterPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const swiperRef = useRef<Swiper | null>(null)
  const { filters, getPageSettings } = useConfigStore()
  const { setSessionState, logActivity, userPreferences } = useSessionStateStore()
  const capturePreview = unifiedCameraUtils.capturePreview
  const welcomeSettings = getPageSettings('welcome')
  const filterTimeout = getPageSettings('filter')?.filterTimeout || 0
  const countdownInstance = useRef<Countdown | null>(null)
  const { projectId } = useParams()

  // Same per-project crop as the capture/camera pages, honoring the operator L/R swap.
  const projectKey = projectId || 'default'
  const cropRatio = useKotakCropStore((s) => s.getProjectCrop(projectKey).ratio)
  const savedPrimary = useKotakCropStore((s) => s.getProjectCrop(projectKey).primary)
  const savedSecondary = useKotakCropStore((s) => s.getProjectCrop(projectKey).secondary)
  const swapped = useKotakCropStore((s) => s.getProjectCrop(projectKey).swapped)
  const { leftKey } = kotakSides(swapped)
  // In single-box (solo) mode preview the camera the guest actually chose; otherwise the left box.
  const previewKey =
    userPreferences.kotakSingleCamera && userPreferences.selectedCamera
      ? userPreferences.selectedCamera
      : leftKey
  const previewCropRect = previewKey === 'primary' ? savedPrimary : savedSecondary

  useEffect((): (() => void) => {
    swiperRef.current = new Swiper('.mainSwiper', {
      loop: false,
      slidesPerView: 3,
      centeredSlides: true,
      spaceBetween: 10,
      direction: 'horizontal'
    })

    swiperRef.current.on('realIndexChange', (swiper) => {
      setActiveIndex(swiper.realIndex)
    })

    if (filters.length > 0) {
      swiperRef.current.slideToLoop(1)
    }

    if (filterTimeout > 0) {
      countdownInstance.current = new Countdown(
        filterTimeout,
        () => setRemainingTime(countdownInstance.current!.getFormattedRemainingSeconds()),
        () => {
          const filterName = filters[activeIndex]?.name
          logActivity(
            `Filter selection timed out. Automatically selected filter: ${filterName || filters[activeIndex]._id}`,
            {
              filterId: filters[activeIndex]._id,
              timeoutDuration: filterTimeout
            }
          )
          continueToPhotoSession()
        }
      )
      countdownInstance.current.start()
    }

    return () => {
      if (swiperRef.current) swiperRef.current.destroy()
      countdownInstance.current?.stop()
    }
  }, [])

  const continueToPhotoSession = async (): Promise<void> => {
    const selectedFilter = filters[activeIndex]
    setSessionState({
      userPreferences: {
        filterId: selectedFilter._id
      }
    })

    const filterName = selectedFilter.name
    logActivity(`Selected filter: ${filterName || selectedFilter._id}`, {
      filterId: selectedFilter._id
    })

    // Kotak: 4R frames — skip strip checkout, go straight to payment (+ recording modal).
    const nextPage = welcomeSettings?.startWithPayment ? 'capture' : 'payment'
    navigate(`/${projectId}/${nextPage}`)
  }

  const slidePrev = (): void => {
    swiperRef.current?.slidePrev()
  }

  const slideNext = (): void => {
    swiperRef.current?.slideNext()
  }

  const handleClick = (index: number): void => {
    swiperRef.current?.slideToLoop(index)
  }

  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBackButton = useCachedImage(backButton)
  const cachedArrowBlue = useCachedImage(arrowBlue)
  const cachedPickRoll = useCachedImage(i18n.language === 'id' ? pickRollId : pickRollEn)

  return (
    <div className="background relative bg-white w-full h-full overflow-hidden">
      <QuitModal showModal={showModal} setShowModal={setShowModal} />
      <div
        className="deco-img absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />
      <div className="marquee-container top-marquee tracking-[0.28em]">
        <div className="marquee-content">
          <span className="font-body-bahela text-uriYellow inline-block text-[38px] italic">
            {i18n.language === 'id'
              ? 'BAHeLA bilik foto lawas untuk menangkap kenangan indahmu bersama orang terkasih di hidupmu'
              : 'BAHeLA vintage photobooth captures beautiful memories with your loved ones'}
          </span>
        </div>
      </div>

      <div className="marquee-container bottom-marquee tracking-[0.28em]">
        <div className="marquee-content">
          <span className="font-body-bahela text-uriYellow inline-block text-[38px] italic">
            {i18n.language === 'id' ? 'Roll film yang sudah dipilih, ' : 'The selected roll film, '}
            <b>{i18n.language === 'id' ? 'tidak dapat diganti' : 'cannot be replaced'}</b>
          </span>
        </div>
      </div>

      <div className="absolute z-[5] left-[50px] top-[102px] flex flex-col items-center">
        <button className="" onClick={() => navigate(`/${projectId}`)} aria-label={t('common.back')}>
          <img
            src={cachedBackButton}
            className="w-[106px] h-[106px] btn-shadow"
            alt={t('common.back')}
          />
        </button>
        <span className="font-body-bahela text-[32px] text-prominentBlue">{t('common.back')}</span>
      </div>

      <div className="absolute top-[250px] left-1/2 -translate-x-1/2 w-screen h-[600px] flex justify-between items-center">
        <button type={'button'} className="w-[163px] h-[141px]" onClick={slidePrev}>
          <img
            src={cachedArrowBlue}
            className="w-[163px] h-[141px] btn-shadow object-contain"
            alt={t('filter.prevFilter')}
          />
        </button>
        <div className="swiper mainSwiper w-full flex items-center justify-center">
          <div className="swiper-wrapper cursor-grab p-0 active:cursor-grabbing focus:cursor-grabbing w-full transition-all duration-300">
            {filters.map((filter, index) => (
              <div
                className="swiper-slide flex flex-col p-0 items-center justify-center transition-all h-[770px] duration-300"
                key={filter._id}
              >
                <div
                  onClick={() => handleClick(index)}
                  className={`rounded-3xl px-[24px] pb-[24px] flex flex-col items-center justify-center btn-shadow transition-all duration-300 w-fit
                                        ${index % 2 === 0 ? 'bg-uriYellow' : 'bg-prominentBlue'}`}
                >
                  <div className="text-center relative w-auto">
                    <h2
                      className={`font-serif-bahela text-black transition-all duration-300
                                                ${activeIndex === index ? 'text-[78px]' : 'text-[42px]'}`}
                    >
                      {filter.name}
                      <span
                        className={`absolute -left-[2px] -top-[2px]
                                                    ${index % 2 === 0 ? 'text-prominentBlue' : 'text-uriYellow'}`}
                      >
                        {filter.name}
                      </span>
                    </h2>
                  </div>
                  <div
                    className={`rounded-lg overflow-hidden relative bg-black
                    ${activeIndex === index ? 'w-[320px] h-[320px]' : 'w-[293px] h-[293px]'}`}
                    style={{ aspectRatio: String(cropRatio) }}
                  >
                    {/* Same cropped box the capture page uses, so the filter looks identical to
                        what the guest will actually shoot. */}
                    <Preview
                      key={`kotak-filter-${filter._id}-${previewKey}`}
                      capturePreview={capturePreview}
                      adjustments={filter.adjustments}
                      postLUTAdjustments={filter.postLUTAdjustments}
                      cameraKey={previewKey}
                      ratio={cropRatio}
                      cropRect={previewCropRect}
                      fillContainer
                      filter={filter._id}
                      previewQuality={{
                        maxWidth: 360,
                        maxHeight: 360,
                        quality: 0.8
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button type={'button'} className="w-[163px] h-[141px] rotate-180" onClick={slideNext}>
          <img
            src={cachedArrowBlue}
            className="w-[163px] h-[141px] btn-shadow object-contain"
            alt={t('filter.nextFilter')}
          />
        </button>
      </div>
      <img className="absolute right-[47px] top-[125px]" src={cachedPickRoll} alt="Pick Roll" />
      <Button
        className="absolute text-[49px] right-[124px] bottom-[155px] flex gap-4 z-[2]"
        textColor="prominentBlue"
        onClick={continueToPhotoSession}
      >
        {filterTimeout > 0 ? `${t('common.next')} (${remainingTime})` : t('common.next')}
      </Button>
    </div>
  )
}

export default FilterPage
